/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {ArrowPathIcon, CheckIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import ModalScrollableClose from "components/ModalScrollableClose";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import UiButton, {UiButtonSize, UiButtonStyle} from "pages/Front/components/Ui/components/UiButton";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {consoleLog} from "utils/utility";
import {SellerAssignSellerRequest} from "types/entities/SellerReservations";
import CompanyFormDropdown, {
    CompanyFormDropdownOption,
} from "pages/Front/Booking/components/CompanyForm/CompanyFormDropdown";
import SellerAction from "store/seller/SellerAction";
import {toast} from "react-toastify";
import {useHistory} from "react-router";
import {closeModal} from "helpers/actions";

interface AssignSellerFormInputs {
    seller_id: number;
    reservation_id: number;
}

interface BookingSellerAssignSellerModalProps {
    reservation_id: number;
}

const BookingSellerAssignSellerModal = (props: BookingSellerAssignSellerModalProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const history = useHistory();
    const defaultValues = {
        seller_id: undefined,
        reservation_id: props.reservation_id,
    };
    const formSchema = yup.object().shape({
        seller_id: yup.number().required(t("Seller is required.")),
        reservation_id: yup.number().required(),
    });
    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm<AssignSellerFormInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(formSchema),
    });

    // TODO: tmp sellers interface
    interface SellerOption {
        id: number,
        name: string,
        last_name: string,
    }

    // TODO: tmp sellers data
    const sellers: SellerOption[] = [
        {id: 22522, name: "Silas", last_name: "Bleisch"},
        {id: 23747, name: "Daniel", last_name: "Bühler"},
        // {id: 25496, name: "John2", last_name: "Doe2"},
        {id: 27032, name: "Back", last_name: "Office"},
        {id: 35790, name: "Lea", last_name: "Petersen"},
    ];

    const sellerOptions: CompanyFormDropdownOption[] = sellers.map((seller) => {
        return {id: seller.id.toString(), name: seller.name + " " + seller.last_name};
    });

    const onErrorHandler = (data: any) => {
        consoleLog("onErrorHandler", data);
    };

    const onSubmitHandler = (data: AssignSellerFormInputs) => {
        consoleLog("onSubmitHandler", data);

        const dataForRequest: SellerAssignSellerRequest = {...data};
        dispatch(SellerAction.fetchAssignSellerToReservation(dataForRequest))
            .then(
                (onResolve) => {
                    dispatch(SellerAction.setReservation(onResolve.reservation));
                    toast.success(t("Seller has been assigned to offer."));
                    dispatch(closeModal());
                },
                (onReject) => {
                    toast.error(t("Error assign seller to offer."));
                },
            ).catch(err => {
            if (err?.status && err?.status === 401) {
                history.replace("/booking/seller/login");
            }
        });
    };

    const setSelectedSeller = (value: any) => {
        setValue("seller_id", value);
    };

    return (
        <CardComponent className={"BookingSellerAssignSellerModal sm:w-[500px] w-full !p-0 border border-gray-200"}>
            <div
                className={"flex flex-row justify-between w-full px-3 py-3 font-bold border-b-2 border-gray-200 bg-gray-100/20 text-gray-900 drop-shadow-sm"}>
                <div className={"flex flex-row items-center"}>
                    <ArrowPathIcon className={clsx("w-5 h-5 mr-1 text-gray-600/70 drop-shadow-xl")} />
                    <h4 className={"font-medium text-gray-700"}>{t("Assign seller to offer")}</h4>
                </div>
                <ModalScrollableClose />
            </div>

            <div
                className={"flex w-full flex-col px-3 pt-3 pb-4 bg-gray-50 overflow-hidden h-auto rounded-bl-lg rounded-br-lg"}>
                <div className={"flex overflow-y-auto h-full w-full"}>
                    <form className={"w-full gap-3 flex flex-col"}
                          onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}>
                        <CardComponent className={"grid grid-cols-2 gap-3"}>
                            <input type={"hidden"} {...register("reservation_id")} />

                            <CompanyFormDropdown
                                className={"col-span-2"}
                                defaultValue={defaultValues?.seller_id ? defaultValues.seller_id : null}
                                errors={errors}
                                label={"Seller"}
                                name={"seller_id"}
                                register={register}
                                required={true}
                                options={sellerOptions}
                                setSelected={setSelectedSeller}
                            />

                        </CardComponent>
                        <UiButton
                            className={"w-full"}
                            icon={<CheckIcon className={"w-4 h-4 flex-none"} />}
                            title={t("Save")}
                            onClick={handleSubmit(onSubmitHandler, onErrorHandler)}
                            style={UiButtonStyle.Add}
                            size={UiButtonSize.Medium}
                        />
                    </form>
                </div>
            </div>
        </CardComponent>
    );
};

export default BookingSellerAssignSellerModal;